import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getWithdrawalGetBalanceQueryKey } from '@/lib/endpoints';
import type { WalletBalanceResponseDto } from '@/lib/models/walletBalanceResponseDto';
import { useRealtimePrivateEvent } from '@/lib/realtime';
import type { BalanceChanged } from '@/lib/realtime/contracts';

/**
 * Keeps the cached wallet balance live off the backend `balance:changed` push
 * (Socket.IO `user:<id>` room → PrivateEventBus). On each event it:
 *  1. patches the cached `balance` immediately — the event carries the
 *     authoritative post-commit figure, so the TopBar pill updates with no
 *     network round-trip; then
 *  2. invalidates the query so `lockedBalance` (not carried on the event)
 *     reconciles on the next fetch.
 *
 * Drives every consumer of `getWithdrawalGetBalanceQueryKey()` — the TopBar,
 * the Withdraw form, and the Wallet page — from one subscription. Mount once
 * inside the {@link PrivateEventBusProvider} (see RealtimePrivateBridge).
 * Mirrors the `useKycRealtime` precedent.
 */
export function useWalletBalanceRealtime(): void {
  const queryClient = useQueryClient();

  // `source` is intentionally omitted: balance:changed is a `backend-only`
  // policy event, so it always arrives via Socket.IO — there is no direct
  // (Centrifugo) variant to disambiguate.
  const handler = useCallback(
    (payload: BalanceChanged) => {
      const queryKey = getWithdrawalGetBalanceQueryKey();
      queryClient.setQueryData<WalletBalanceResponseDto>(queryKey, (prev) =>
        prev ? { ...prev, balance: payload.balance } : prev,
      );
      void queryClient.invalidateQueries({ queryKey });
    },
    [queryClient],
  );

  useRealtimePrivateEvent('balance:changed', handler);
}
